import { useEffect, useState, useMemo, useCallback } from 'react';
import { Save, Loader2, Clock } from 'lucide-react'; 
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { 
  PrefTypeConst, 
  PrefCategoryLabel,
  ListingCategoryConst, 
} from '../../../constants';
import { 
  BiddingScheduleFlowSteps as OriginalFlowSteps, 
  ScheduleTypeLabel as OriginalLabels,
  BusinessDayTypeConst,
  BusinessDayTypeOptions,
  InitialBiddingScheduleTimes, 
  ScheduleTypeConst,
  TODAY_TASK_TYPES 
} from '../../../constants/BiddingScheduleConst';
import { BiddingScheduleCalendar } from '../../../components/admin/BiddingSchedule/BiddingScheduleCalendar';
import { useAuth } from '../../../contexts/AuthContext';
import { useGetMonthSchedulesQuery, useUpsertSchedulesMutation } from '../../../hooks/useCalendarQuery';
import { type BiddingScheduleUpsertRequest, type BiddingScheduleDayDto } from '../../../types/BiddingScheduleTypes';
import { useAppDialog } from '../../../hooks/useAppDialog';
import { UserRole } from '../../../types/UserRole';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

export const BiddingSchedulePage = () => {
  //----------------
  // Context / Hooks
  //----------------
  const queryClient = useQueryClient();
  // ログイン中のユーザー情報を取得
  const { currentUser } = useAuth();
  // スケジュール設定（入札開催や時刻）の保存APIを呼び出すためのミューテーション
  const upsertMutation = useUpsertSchedulesMutation();

  const { showConfirm, OverlayElement } = useAppDialog();
  
  // 権限の判定（管理者は編集可、それ以外は閲覧専用）
  const isAdmin = currentUser?.role === UserRole.Admin;
  const isReadOnly = !isAdmin;

  //----------------
  // State
  //----------------
  // カレンダーで選択された日付の配列（単一選択、または複数一括選択用）
  const [selectedDates, setSelectedDates] = useState<Dayjs[]>([dayjs()]); 
  // カレンダーの表示月を管理
  const [viewDate, setViewDate] = useState<Dayjs>(dayjs()); 
  // 初期化完了フラグ（ユーザー情報等の読み込み完了後にAPIを叩くための制御に使用）
  const [isInitialized, setIsInitialized] = useState(false); 
  // 選択された日付の営業日区分（営業日 or 休業日）
  const [businessStatus, setBusinessStatus] = useState<number>(BusinessDayTypeConst.BUSINESS_DAY);

  // 県内/県外それぞれの「入札開催」トグルの状態（true: 開催, false: 未開催）
  const [holdingStatus, setHoldingStatus] = useState<Record<number, boolean>>({
    [PrefTypeConst.INTERNAL]: false,
    [PrefTypeConst.EXTERNAL]: false,
  });

  // 入札時刻設定の初期値（空の時刻データ）を生成するヘルパー関数
  const createEmptyScheduleData = () => JSON.parse(JSON.stringify(InitialBiddingScheduleTimes));
  
  // 県区分別・工程別の設定時刻データを保持
  const [scheduleData, setScheduleData] = useState<Record<number, Record<number, string>>>(createEmptyScheduleData());
  // 既に実行済みの実績時刻（採番時刻、落札確定時刻など）を保持
  const [executedData, setExecutedData] = useState<Record<number, Record<number, string | null>>>({ [PrefTypeConst.INTERNAL]: {}, [PrefTypeConst.EXTERNAL]: {} });

  // 現在選択されている設定が休業日かどうかを判定
  const isNonBusinessDay = businessStatus === BusinessDayTypeConst.NON_BUSINESS_DAY;

  //----------------
  // 状態判定 
  //----------------
  
  // 選択された日付に「過去日」が含まれているかを判定
  // （過去日が含まれる場合は、変更できないよう画面全体をロックする）
  const isPastSelected = useMemo(() => {
    return selectedDates.some(d => d.isBefore(dayjs(), 'day'));
  }, [selectedDates]);

  // 県区分別（県内/県外）に「落札結果確定」の実績が記録されているかを判定
  // （確定済みの場合は設定を変更できないようトグル等をロックする）
  const isConfirmed = useMemo(() => ({
    [PrefTypeConst.INTERNAL]: !!executedData[PrefTypeConst.INTERNAL]?.[ScheduleTypeConst.BIDDING_CONFIRM],
    [PrefTypeConst.EXTERNAL]: !!executedData[PrefTypeConst.EXTERNAL]?.[ScheduleTypeConst.BIDDING_CONFIRM],
  }), [executedData]);

  // どちらかの県に1つでも落札確定の実績があるか判定
  // （画面上部の注意書きテキストの表示切り替えに使用）
  const hasAnyExecutedData = useMemo(() => {
    return isConfirmed[PrefTypeConst.INTERNAL] || isConfirmed[PrefTypeConst.EXTERNAL];
  }, [isConfirmed]);
  
  // 権限ごとに表示する工程（ステップ）の配列を決定する
  const displaySteps = useMemo(() => {
    const role = currentUser?.role;

    if (role === UserRole.Buyer) {
      // 買手の場合
      return [
        ScheduleTypeConst.PUBLISHED_START_AT, // ※出品予定公開
        ScheduleTypeConst.BIDDING_START,      // ※入札開始
        ScheduleTypeConst.BIDDING_END         // ※入札終了
      ];
    }

    if (role === UserRole.Seller || role === UserRole.Farmer) {
      // 売手・生産者の場合
      return [
        ScheduleTypeConst.APPLIED_END,                // ※提出締切
        ScheduleTypeConst.LOWEST_PRICE_DECISION_END,  // ※最低落札値締切
        ScheduleTypeConst.BIDDING_START,              // ※入札開始
        ScheduleTypeConst.BIDDING_END                 // ※入札終了
      ];
    }

    // 管理者（Admin）の場合はすべて表示
    return OriginalFlowSteps;
  }, [currentUser?.role]);

  //----------------
  // Memo / Data Fetching
  //----------------
  // カレンダーの表示月（viewDate）から、APIリクエスト用の年月パラメータ（YYYYMM形式）を生成
  const yearMonth = useMemo(() => parseInt(viewDate.format("YYYYMM")), [viewDate]);
  
  // 表示月の1ヶ月分のスケジュールデータを取得
  // （ユーザー情報が存在し、初期化が完了している場合のみリクエストを実行）
  const { data: monthData, isLoading: isDataLoading, isFetching, error } = useGetMonthSchedulesQuery(
    yearMonth, 
    ListingCategoryConst.BIDDING,
    { enabled: !!currentUser && isInitialized } 
  );
  
  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['monthSchedules'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  //----------------
  // Effect (データ同期)
  //----------------
  // ユーザー情報を取得後、初期化完了フラグを立ててAPI通信を許可する
  useEffect(() => {
    if (!currentUser || isInitialized) return;
    setIsInitialized(true); 
  }, [currentUser, isInitialized]);

  // データ取得時のエラーハンドリング
  useEffect(() => {
    if (error) {
      toast.error(error instanceof Error ? error.message : "スケジュールの取得に失敗しました");
    }
  }, [error]);

  // カレンダーの選択日付や、APIからの月間データが変化した際に実行
  // 選択された日（先頭の日付）のデータを画面の各入力フォームのState（営業日、開催トグル、設定時刻、実績）に反映させる
  useEffect(() => {
    const typedMonthData = monthData as Record<string, BiddingScheduleDayDto> | undefined;
    if (!typedMonthData || selectedDates.length === 0 || !selectedDates[0]) return;
    
    // 選択された日付をキーにして、月間データの中から該当日のデータを抽出
    const dateKey = selectedDates[0].format("YYYY-MM-DD");
    const dayData = typedMonthData[dateKey];

    // 次にStateにセットするための器を初期化
    let nextScheduleData = createEmptyScheduleData(); 
    let nextExecutedData: Record<number, Record<number, string | null>> = { [PrefTypeConst.INTERNAL]: {}, [PrefTypeConst.EXTERNAL]: {} };
    let nextBusinessStatus: number = BusinessDayTypeConst.BUSINESS_DAY;
    let nextHolding: Record<number, boolean> = { [PrefTypeConst.INTERNAL]: false, [PrefTypeConst.EXTERNAL]: false };

    // 該当日のデータが存在すれば、器に値を詰め込んでいく
    if (dayData) {
      nextBusinessStatus = dayData.businessDay;
      if (dayData.holdingStatus) {
        Object.entries(dayData.holdingStatus).forEach(([k, v]) => { nextHolding[Number(k)] = v as boolean; });
      }
      if (dayData.scheduleData) {
        Object.entries(dayData.scheduleData).forEach(([prefKey, times]) => {
          const pKey = Number(prefKey);
          if (nextScheduleData[pKey]) {
            Object.entries(times as Record<number, string>).forEach(([typeKey, timeStr]) => {
              if (timeStr) nextScheduleData[pKey][Number(typeKey)] = timeStr;
            });
          }
        });
      }
      if (dayData.executedData) {
        Object.entries(dayData.executedData).forEach(([prefKey, times]) => {
          const pKey = Number(prefKey);
          nextExecutedData[pKey] = {};
          Object.entries(times as Record<number, string | null>).forEach(([typeKey, timeStr]) => {
            nextExecutedData[pKey][Number(typeKey)] = timeStr;
          });
        });
      }
    }
    
    // 抽出・整形したデータを一斉にStateに反映
    setBusinessStatus(nextBusinessStatus);
    setHoldingStatus(nextHolding);
    setScheduleData(nextScheduleData);
    setExecutedData(nextExecutedData);
  }, [selectedDates, monthData]);

  //----------------
  // Handlers
  //----------------
  // 営業日区分の変更ハンドラー
  const handleBusinessStatusChange = useCallback((status: number) => {
    setBusinessStatus(status);
    // 休業日に変更された場合は、県内・県外ともに「入札開催」を強制的にオフにする
    if (status === BusinessDayTypeConst.NON_BUSINESS_DAY) {
      setHoldingStatus({ [PrefTypeConst.INTERNAL]: false, [PrefTypeConst.EXTERNAL]: false });
    }
  }, []);

  // カレンダー上の日付クリック時のハンドラー
  const handleDateToggle = useCallback((date: Dayjs, isBulkMode: boolean) => {
    setSelectedDates(prev => {
      // 単一選択モードの場合は、クリックした日付のみをセット
      // if (!isBulkMode) return [date];
      if (isReadOnly || !isBulkMode) return [date];

      // 一括モードの場合は、日付選択済みなら解除、未選択なら選択のトグル動作
      const isAlreadySelected = prev.some(d => d.isSame(date, 'day'));
      return isAlreadySelected ? (prev.length > 1 ? prev.filter(d => !d.isSame(date, 'day')) : prev) : [...prev, date];
    });
  }, []);

  // 設定保存ボタンのクリックハンドラー
  const handleSave = useCallback(() => {
    // 保存対象の日付が選択されていない場合はガード
    if (selectedDates.length === 0) { toast.error("日付を選択してください"); return; }

    // 単一日か複数日（一括）かで確認メッセージを出し分ける
    const confirmMsg = selectedDates.length > 1 
        ? `${selectedDates.length}日分の設定を一括更新します。よろしいですか？`
        : `${selectedDates[0].format('YYYY年MM月DD日')} の設定を更新します。よろしいですか？`;

    showConfirm({
      title: "入札スケジュール管理",
      message: confirmMsg,
      onConfirm: async () => {
        // APIに送信するリクエストボディを構築
        const payload: BiddingScheduleUpsertRequest = {
          targetDates: selectedDates.map(d => parseInt(d.format("YYYYMMDD"))),
          businessDay: businessStatus,
          listingCategory: ListingCategoryConst.BIDDING,
          settings: [PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL].map(pref => ({
            prefectureType: pref,
            isHolding: holdingStatus[pref], 
            times: scheduleData[pref] || {} 
          }))
        };

        try {
          // 構築したデータでAPIを呼び出し、保存処理を実行
          await upsertMutation.mutateAsync(payload);
          toast.success("設定を保存しました");
        } catch (e) { 
          //toast.error("保存に失敗しました"); 
        }
      }
    });
  }, [selectedDates, businessStatus, holdingStatus, scheduleData, upsertMutation, showConfirm]);

  const mainGridClass = "grid grid-cols-1 sm:grid-cols-[1fr_110px_minmax(160px,180px)_minmax(160px,180px)] gap-x-4 lg:gap-x-6 items-center";

  //----------------
  // Render Helpers
  //----------------
  // 初期化前、またはデータ取得中（初回ロード時）はローディング画面を表示
  const isPageLoading = !isInitialized || (isDataLoading && !monthData);
  if (isPageLoading) return <div className="bg-slate-50 min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>;

  return (
    <div className="bg-slate-50 min-h-screen font-sans overflow-x-hidden text-slate-800 relative">
      
      {/* 共通背景レイヤー */}
      {OverlayElement}

      <div className="max-w-350 mx-auto px-4 lg:px-8 pt-4 lg:pt-10 pb-4 flex flex-col lg:h-screen">
        
        {/* ヘッダー */}
        <header className="mb-4 lg:mb-6 shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-2">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-800">入札スケジュール管理</h1>
          </div>
          {/* 管理者以外は「保存ボタン」を非表示にする */}
          {!isReadOnly && (
            <button 
              type="button" 
              onClick={handleSave} 
              // 過去日の場合は保存ボタンを無効化
              disabled={upsertMutation.isPending || isPastSelected}
              className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md transition active:scale-95 flex items-center justify-center gap-2 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {upsertMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              設定を保存
            </button>
          )}
        </header>

        <main className="flex-1 min-h-0 pb-2">
          <div className="bg-white border border-slate-200 rounded-3xl lg:rounded-4xl shadow-sm flex flex-col lg:flex-row items-stretch overflow-hidden h-fit lg:max-h-full">
            
            {/* 左側：カレンダー */}
            <div className="w-full lg:w-110 bg-slate-50/50 border-b lg:border-b-0 lg:border-r border-slate-100 p-4 lg:p-6 flex items-center justify-center shrink-0">
              <div className="w-full">
                <BiddingScheduleCalendar 
                  selectedDates={selectedDates} onDateToggle={handleDateToggle} 
                  onReset={() => { setSelectedDates([dayjs()]); setViewDate(dayjs()); }}
                  viewDate={viewDate} onViewDateChange={setViewDate} monthData={monthData}
                />
              </div>
            </div>

            {/* 右側：メイン設定エリア */}
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 relative overflow-x-hidden sm:overflow-x-auto custom-scrollbar">
              <div className="p-3 lg:p-5 flex-1 flex flex-col min-w-0 sm:min-w-162.5">
                
                {/* データの再取得中や保存処理中は、操作できないようにオーバーレイを表示 */}
                {(isDataLoading || upsertMutation.isPending) && (
                  <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[1px]">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                )}

                {/* 上部：メイン設定パネル */}
                <div className={`${mainGridClass} px-1 lg:px-3 border-b border-slate-100 pb-2.5 mb-1`}>
                  <div className="text-base font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                    設定項目
                    {selectedDates.length === 1 && selectedDates[0] && (
                      <span className="text-base font-bold text-indigo-600 ml-1 whitespace-nowrap">{selectedDates[0].format('YYYY年MM月DD日分')}</span>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    {/* 過去日の場合は休業日設定のセレクトボックスをロック */}
                    <select 
                      value={businessStatus} 
                      onChange={(e) => handleBusinessStatusChange(Number(e.target.value))} 
                      // disabled={isPastSelected}
                      // 過去日、または閲覧専用ユーザーはロック
                      disabled={isPastSelected || isReadOnly}
                      className="w-full bg-white border border-slate-200 text-sm font-black rounded-lg px-2 py-1.5 outline-none text-slate-600 shadow-sm disabled:bg-slate-100 disabled:text-slate-700 disabled:appearance-none"
                    >
                      {BusinessDayTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>

                  {/* 県内・県外それぞれの「入札開催」トグルスイッチ */}
                  {[PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL].map(pref => (
                    <div key={`header-${pref}`} className="flex sm:flex flex-col gap-1.5 bg-white px-3 py-2 rounded-2xl border border-slate-200 shadow-sm items-center">
                      <span className={`text-[11px] font-black leading-none text-indigo-600 text-center`}>
                        {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}入札開催
                      </span>
                      <div className="flex flex-col items-center gap-0.5">
                        <button 
                          type="button" 
                          // 閲覧専用ユーザーはトグルスイッチをロック
                          // 過去日、休業日、または既に確定実績がある場合はトグルを固定
                          // disabled={isNonBusinessDay || isPastSelected || isConfirmed[pref as keyof typeof isConfirmed]} 
                          disabled={isNonBusinessDay || isPastSelected || isConfirmed[pref as keyof typeof isConfirmed] || isReadOnly}
                          onClick={() => setHoldingStatus(prev => ({ ...prev, [pref]: !prev[pref] }))} 
                          className={`relative inline-flex h-4.5 w-8.5 shrink-0 items-center rounded-full transition-colors ${holdingStatus[pref] ? 'bg-indigo-600' : 'bg-slate-300'} disabled:opacity-30`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${holdingStatus[pref] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="px-2 py-1.5">
                  <p className="text-[11px] font-bold text-red-500 leading-relaxed">
                      {isReadOnly
                        ? "※この画面は参照専用です。設定の変更はできません。"
                        : isPastSelected
                          ? "※過去日付選択中のため、すべての項目が変更不可（参照のみ）となります。"
                            : isNonBusinessDay
                              ? "※休業日のため、設定は行えません。"
                              : hasAnyExecutedData 
                                ? "※落札確定済のため、入札開催スイッチは固定されています。"
                                : "※営業日であれば全項目の入力が可能です。※「入札開催」がオフの場合、入札日項目はロックされます。"}
                  </p>
                </div>

                {/* 工程リストエリア：時刻の設定フォーム */}
                <div className="flex-1 overflow-y-auto sm:pr-1 custom-scrollbar pt-1">
                  <div className="space-y-1.5 mb-4">
                    {displaySteps.map((type) => {
                      const typeNum = type as number;
                      const isTodayTask = TODAY_TASK_TYPES.includes(typeNum);

                      return (
                        <div key={type} className={`${mainGridClass} border rounded-xl p-2.5 bg-white border-slate-100 hover:border-indigo-100 shadow-sm transition-all`}>
                          <div className="min-w-0 flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isTodayTask ? 'bg-indigo-400' : 'bg-emerald-400'}`} />
                            <div className={`text-sm font-bold text-slate-700 truncate`}>{OriginalLabels[typeNum as keyof typeof OriginalLabels]}</div>
                          </div>
                          <div className="hidden lg:block" /> 
                          
                          {[PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL].map(pref => {
                            // 過去日や休業日でなく、かつ（当日タスクではない OR 入札開催がオン）の場合のみ編集可能とする制御
                            // 閲覧専用ユーザーをブロック
                            // const canEdit = !isPastSelected && !isNonBusinessDay && (!isTodayTask || holdingStatus[pref]);
                            const canEdit = !isPastSelected && !isNonBusinessDay && (!isTodayTask || holdingStatus[pref]);
                            const timeValue = scheduleData[pref]?.[typeNum] || "";

                            return (
                                  <div 
                                    key={`${type}-${pref}`} 
                                    // opacity-40を外し、編集不可時は背景を少しグレー(bg-slate-100)にして視認性を確保
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                      canEdit 
                                        ? 'bg-slate-50 border-slate-200 focus-within:border-indigo-500 focus-within:bg-white shadow-inner' 
                                        : 'bg-slate-100 border-slate-200'
                                    }`}
                                  >
                                    <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                      {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                                    </span>                                    
                                    {!isReadOnly ? (
                                      // 編集可能な場合は input(time) を表示
                                      <input 
                                        type="time" 
                                        disabled={!canEdit} 
                                        value={timeValue} 
                                        onChange={(e) => setScheduleData(prev => ({ ...prev, [pref]: { ...prev[pref], [typeNum]: e.target.value } }))} 
                                        className="bg-transparent border-none p-0 text-sm font-black text-slate-800 outline-none w-full cursor-pointer disabled:text-slate-400"
                                      />
                                    ) : (
                                      // ▼ 閲覧専用ユーザー：spanでテキスト表示
                                      <span className={`text-sm w-full text-center ${
                                        (!isNonBusinessDay && (!isTodayTask || holdingStatus[pref]))
                                          ? (timeValue ? 'font-black text-slate-800' : 'font-black text-slate-300') // 有効な項目（くっきり）
                                          : 'font-medium text-slate-300' // 無効な項目（休業日や過去日などは全体をグレーアウト）
                                      }`}>
                                        {timeValue || "--:--"}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                        </div>
                      );
                    })}
                  </div>
                  {/* 管理者（編集可能なユーザー）のみ表示 ▼▼▼ */}
                  {!isReadOnly && (
                    <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-200 space-y-2">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest px-2 mb-2 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> 実行済み時刻 (実績)
                      </h3>
                      
                      {/* 採番実績 */}
                      <div className={`${mainGridClass} bg-slate-100/50 border border-slate-200/50 rounded-2xl p-2`}>
                        <div className="flex items-center gap-3 min-w-0 px-2">
                          <div className="text-sm font-black text-slate-700 uppercase leading-tight">次回入札日分入札番号採番時刻</div>
                        </div>
                        <div className="hidden lg:block" />
                        {[PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL].map(pref => (
                          <div key={`exec1-${pref}`} className="flex items-center px-3 py-1.5 rounded-xl border bg-white shadow-sm border-slate-200">
                            <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                            </span>
                            <span className="text-lg font-black text-slate-600 tracking-tight leading-none">
                              {executedData[pref]?.[ScheduleTypeConst.NUMBERING] || "--:--"}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* 確定実績 */}
                      <div className={`${mainGridClass} bg-slate-100/50 border border-slate-200/50 rounded-2xl p-2`}>
                        <div className="flex items-center gap-3 min-w-0 px-2">
                          <div className="text-sm font-black text-slate-700 uppercase leading-tight">落札結果確定時刻</div>
                        </div>
                        <div className="hidden lg:block" />
                        {[PrefTypeConst.INTERNAL, PrefTypeConst.EXTERNAL].map(pref => (
                          <div key={`exec7-${pref}`} className="flex items-center px-3 py-1.5 rounded-xl border bg-white shadow-sm border-slate-200">
                            <span className="text-[11px] font-bold text-slate-400 shrink-0 w-8">
                                {PrefCategoryLabel[pref as keyof typeof PrefCategoryLabel]}
                            </span>
                            <span className="text-lg font-black text-slate-600 tracking-tight leading-none">
                              {executedData[pref]?.[ScheduleTypeConst.BIDDING_CONFIRM] || "--:--"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching || isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-50 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default BiddingSchedulePage;